import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, ArrowRight, ArrowLeft, Calendar, DollarSign } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useSmartCTA } from "@/hooks/useSmartCTA";
import api from "@/lib/api";
import JobPhotoGallery from "@/components/uploads/JobPhotoGallery";
import { categoryLabel, subcategoryLabel } from "@/lib/catalog";
import { budgetText, jobLocationText } from "@/lib/jobDisplay";
import { formatDate } from "@/lib/dates";

export default function PublicJobDetail() {
  const { t } = useTranslation();
  const cta = useSmartCTA();
  const { job_id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/jobs/public/${job_id}`)
      .then((r) => setJob(r.data.job))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [job_id]);

  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="public-job-detail-page">
      <Navbar />
      <main className="flex-1 section">
        <div className="container-app">
          <Link to="/open-jobs" className="fx-btn fx-btn--ghost text-sm mb-6 inline-flex" data-testid="public-job-back">
            <ArrowLeft size={16} /> {t("publicJobs.back")}
          </Link>

          {loading ? (
            <p className="text-smoke">{t("common.loading")}</p>
          ) : notFound || !job ? (
            <p className="text-smoke" data-testid="public-job-notfound">{t("publicJobs.notFound")}</p>
          ) : (
            <div className="grid lg:grid-cols-[1.6fr_1fr] gap-8">
              <div>
                <span className="chip mb-3">{categoryLabel(t, job.category)}</span>
                {job.subcategory && (
                  <span className="chip mb-3 ml-2">{subcategoryLabel(t, job.subcategory)}</span>
                )}
                <h1 className="font-display text-3xl md:text-4xl font-bold text-worker text-balance">{job.title}</h1>

                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-smoke">
                  {(job.location?.city || job.location?.state) && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={14} /> {[job.location.city, job.location.state].filter(Boolean).join(", ")}
                    </span>
                  )}
                  {(job.budget_min > 0 || job.budget_max > 0) && (
                    <span className="inline-flex items-center gap-1">
                      <DollarSign size={14} /> {budgetText(t, job.budget_min, job.budget_max)}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <Calendar size={14} /> {t("publicJobs.postedOn", { date: formatDate(job.created_at) })}
                  </span>
                </div>

                {(job.preferred_start_date || job.preferred_end_date) && (
                  <p className="mt-2 text-sm text-smoke">
                    {t("publicJobs.preferredDates", {
                      start: formatDate(job.preferred_start_date) || "—",
                      end: formatDate(job.preferred_end_date) || "—",
                    })}
                  </p>
                )}

                <p className="mt-6 text-worker whitespace-pre-line leading-relaxed">{job.description}</p>

                {job.photos?.length > 0 && (
                  <div className="mt-6">
                    <JobPhotoGallery urls={job.photos} testid="public-job-photos" />
                  </div>
                )}
              </div>

              <aside className="lg:sticky lg:top-24 h-fit">
                {/*
                  The card answers to whoever is reading it.

                  It used to print the anonymous pitch unconditionally — "create a
                  FixyBee account to send a quote" — to a signed-in Master FixyBee
                  whose own navbar, three centimetres above, said "Dashboard" and
                  "Log out". `cta.quoteJob` returns the state and the destination
                  together so the sentence and the link can never disagree again;
                  the copy keys below are named after the states, not after the
                  anonymous case with exceptions bolted on.
                */}
                {(() => {
                  const q = cta.quoteJob(job);
                  // Auth has not answered yet. A card with no sentence for half a
                  // second is honest; the anonymous pitch shown to a signed-in
                  // FixyBee for half a second is the same bug on a timer.
                  if (q.state === "pending") {
                    return (
                      <div className="card-base p-6" data-testid="public-job-cta-card-pending">
                        <div className="skeleton h-6 w-2/3" />
                        <div className="skeleton h-4 w-full mt-3" />
                        <div className="skeleton h-10 w-full mt-4" />
                      </div>
                    );
                  }
                  return (
                    <div className="card-base p-6" data-testid={`public-job-cta-card-${q.state}`}>
                      <h2 className="font-display text-xl font-bold text-worker">
                        {t(`publicJobs.card.${q.state}.title`)}
                      </h2>
                      <p className="mt-2 text-sm text-smoke">
                        {t(`publicJobs.card.${q.state}.body`)}
                      </p>
                      <Link
                        to={q.to}
                        className={`w-full mt-4 justify-center ${q.state === "quote" ? "fx-btn fx-btn--primary" : "fx-btn fx-btn--secondary"}`}
                        data-testid="public-job-cta"
                      >
                        {t(`publicJobs.card.${q.state}.cta`)} <ArrowRight size={18} />
                      </Link>
                    </div>
                  );
                })()}
              </aside>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
